"use client";

import { publicStorePath } from "@/lib/storefront/public-url";
import { Suspense, useCallback, useMemo, useEffect, useTransition } from "react";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import type { VariantSelectionSectionProps } from "./types";
import { VariantSelector } from "./variant-selector";
import { VariantNameSelector } from "./variant-name-selector";
import {
	groupVariantsByAttributes,
	findMatchingVariant,
	getSelectionsFromVariant,
	getDefaultSelections,
	getOptionsForAttribute,
	getAdjustedSelections,
	getUnavailableAttributeInfo,
	type SaleorVariant,
} from "./utils";
import { VariantAttributeBadges, extractOptionalAttributes } from "./optional-attributes";

/** `useSearchParams()` can lag on the first client paint; the real URL still has the query. */
function readVariantIdFromWindow(): string | undefined {
	if (typeof window === "undefined") return undefined;
	try {
		const v = new URLSearchParams(window.location.search).get("variant");
		return v ?? undefined;
	} catch {
		return undefined;
	}
}

/** Same as `variant` — attribute slugs (dosage, package, etc.) must also read from `window` on first paint. */
function readQueryParamFromWindow(key: string): string | null {
	if (typeof window === "undefined") return null;
	try {
		return new URLSearchParams(window.location.search).get(key);
	} catch {
		return null;
	}
}

/**
 * Main container for variant selection with multiple attributes.
 *
 * Handles products with multiple variant attributes (e.g., Color AND Size).
 * Each attribute gets its own selector, and selections are combined to find
 * the matching variant.
 *
 * ## How it works
 *
 * 1. Groups variants by their attributes (Color, Size, etc.)
 * 2. Shows a selector for each attribute
 * 3. Tracks selections in URL params (e.g., ?color=black&size=m)
 * 4. When all attributes selected, finds and sets the variant param
 * 5. Updates availability based on other selections (e.g., "XL" grayed out if not available in Black)
 *
 * ## URL Structure
 *
 * - `?variant=abc123` - Selected variant ID (for cart/checkout)
 * - `?color=black&size=m` - Individual attribute selections (for UX)
 *
 * ## Customization
 *
 * Replace entirely with a custom implementation:
 * ```tsx
 * <VariantSelectionSection variants={variants}>
 *   <MyCustomVariantPicker variants={variants} />
 * </VariantSelectionSection>
 * ```
 */

function VariantSelectionSectionSuspenseFallback() {
	return <div className="bg-muted/60 min-h-24 w-full max-w-md animate-pulse rounded-lg" aria-hidden />;
}

/**
 * `useSearchParams()` must sit under a Suspense boundary (Next.js static/prerender).
 * The parent Suspense around the async variant shell is not always enough for CSR bailout.
 */
export function VariantSelectionSection(props: VariantSelectionSectionProps) {
	return (
		<Suspense fallback={<VariantSelectionSectionSuspenseFallback />}>
			<VariantSelectionSectionInner {...props} />
		</Suspense>
	);
}

function VariantSelectionSectionInner({
	variants,
	selectedVariantId,
	productSlug,
	channel,
	locale: localeProp,
	resolvedByVariantId,
	resolvedMoneyByKey,
	children,
}: VariantSelectionSectionProps) {
	const router = useRouter();
	const searchParams = useSearchParams();
	const { locale: routeLocale } = useParams<{ locale: string }>();
	const locale = localeProp ?? routeLocale;
	const [isPending, startTransition] = useTransition();

	// Group variants by attributes (always call hooks unconditionally)
	const attributeGroups = useMemo(() => groupVariantsByAttributes(variants as SaleorVariant[]), [variants]);

	// Resolve variant id: hook first, then real location (hydration), then RSC prop.
	// useSearchParams() can be empty on the first client paint while the URL bar still has ?variant=…;
	// window fallback avoids SSR/client drift and React hydration errors.
	const variantFromUrlOrServer = useMemo(() => {
		const fromHook = searchParams.get("variant");
		if (fromHook && variants.some((variant) => variant.id === fromHook)) return fromHook;
		const fromWindow = readVariantIdFromWindow();
		if (fromWindow && variants.some((variant) => variant.id === fromWindow)) return fromWindow;
		if (attributeGroups.some((group) => searchParams.get(group.slug) ?? readQueryParamFromWindow(group.slug)))
			return undefined;
		return variants.some((variant) => variant.id === selectedVariantId) ? selectedVariantId : undefined;
	}, [searchParams, selectedVariantId, variants, attributeGroups]);

	// Get current selections from URL params, selected variant, or auto-defaults
	const currentSelections = useMemo(() => {
		const selections: Record<string, string> = {};
		// The variant ID also drives price and cart, so it is authoritative over stale attribute params.
		if (variantFromUrlOrServer)
			return getSelectionsFromVariant(variants as SaleorVariant[], variantFromUrlOrServer);

		// First, try to get from URL params (hook + window fallback for hydration parity with SSR)
		for (const group of attributeGroups) {
			const paramValue = searchParams.get(group.slug) ?? readQueryParamFromWindow(group.slug);
			if (paramValue) {
				selections[group.slug] = paramValue;
			}
		}

		// If still no selections, auto-select lowest dosage + first package
		if (Object.keys(selections).length === 0 && attributeGroups.length > 0) {
			return getDefaultSelections(attributeGroups, variants as SaleorVariant[]);
		}

		return selections;
	}, [attributeGroups, searchParams, variantFromUrlOrServer, variants]);

	// Find currently matching variant and extract its non-selection attributes
	const currentVariantId = useMemo(() => {
		if (variantFromUrlOrServer) return variantFromUrlOrServer;
		return findMatchingVariant(variants as SaleorVariant[], currentSelections, attributeGroups);
	}, [variantFromUrlOrServer, variants, currentSelections, attributeGroups]);

	const optionalAttributes = useMemo(
		() => extractOptionalAttributes(variants, currentVariantId),
		[variants, currentVariantId],
	);

	// Handle selection change with smart adjustment
	const handleSelect = useCallback(
		(attributeSlug: string, optionId: string) => {
			// Get adjusted selections - clears conflicting selections if needed
			const newSelections = getAdjustedSelections(
				variants as SaleorVariant[],
				currentSelections,
				attributeSlug,
				optionId,
				attributeGroups,
			);

			// Build URL params
			const params = new URLSearchParams(searchParams.toString());

			for (const group of attributeGroups) params.delete(group.slug);
			for (const [slug, value] of Object.entries(newSelections)) {
				if (value) params.set(slug, value);
			}

			// Try to find matching variant
			const matchingVariantId = findMatchingVariant(
				variants as SaleorVariant[],
				newSelections,
				attributeGroups,
			);

			if (matchingVariantId) {
				params.set("variant", matchingVariantId);
			} else {
				// Remove variant param if no exact match yet
				params.delete("variant");
			}

			// Use transition to keep UI responsive during navigation
			startTransition(() => {
				router.push(`${publicStorePath(locale, `/products/${productSlug}?${params.toString()}`)}`, {
					scroll: false,
				});
			});
		},
		[currentSelections, variants, locale, productSlug, router, attributeGroups, searchParams],
	);

	// Check if any attribute group is completely unavailable
	const unavailableInfo = useMemo(
		() => getUnavailableAttributeInfo(variants as SaleorVariant[], attributeGroups, currentSelections),
		[variants, attributeGroups, currentSelections],
	);

	// Sync auto-selected defaults to URL so price/image/cart reflect the selection
	useEffect(() => {
		if (!currentVariantId) return;
		const urlVariant = searchParams.get("variant") ?? readVariantIdFromWindow();
		if (urlVariant === currentVariantId) return; // Already in URL, nothing to sync

		// Check if any attribute params are already in URL (hook + window — same as currentSelections)
		const hasUrlParams = attributeGroups.some(
			(g) => searchParams.get(g.slug) ?? readQueryParamFromWindow(g.slug),
		);
		if (hasUrlParams) return;

		// Auto-selections were applied but not yet in URL — sync them
		const params = new URLSearchParams(searchParams.toString());
		for (const [slug, value] of Object.entries(currentSelections)) {
			if (value) params.set(slug, value);
		}
		params.set("variant", currentVariantId);

		startTransition(() => {
			router.replace(`${publicStorePath(locale, `/products/${productSlug}?${params.toString()}`)}`, {
				scroll: false,
			});
		});
	}, [currentVariantId, searchParams, attributeGroups, currentSelections, locale, productSlug, router]);

	// Development warning for name-based fallback
	useEffect(() => {
		if (process.env.NODE_ENV === "development" && attributeGroups.length === 0 && variants.length > 1) {
			console.warn(
				`[VariantSelectionSection] Product "${productSlug}" has ${variants.length} variants but no structured attributes. ` +
					`Using name-based fallback selector. For better UX (Dosierung + Gebinde picker, piece pricing, cross-filtering), ` +
					`configure VARIANT_SELECTION attributes (e.g. dosage/package) in Saleor Dashboard.`,
			);
		}
	}, [attributeGroups.length, variants.length, productSlug]);

	// Handle variant selection for name-based fallback
	const handleVariantSelect = useCallback(
		(variantId: string) => {
			const params = new URLSearchParams(searchParams.toString());
			params.set("variant", variantId);
			startTransition(() => {
				router.push(`${publicStorePath(locale, `/products/${productSlug}?${params.toString()}`)}`, {
					scroll: false,
				});
			});
		},
		[locale, productSlug, router, searchParams],
	);

	// If children provided, render them instead (full override)
	if (children) {
		return <>{children}</>;
	}

	// Skip rendering if only one variant (no selection needed)
	if (variants.length <= 1) {
		return null;
	}

	// Fallback: variants without structured attributes use name-based selector
	if (attributeGroups.length === 0) {
		return (
			<div className="space-y-4 py-1 sm:space-y-6 sm:py-2">
				<VariantNameSelector
					variants={variants}
					selectedVariantId={variantFromUrlOrServer}
					onSelect={handleVariantSelect}
					isPending={isPending}
					locale={locale}
					channel={channel}
					resolvedByVariantId={resolvedByVariantId}
					resolvedMoneyByKey={resolvedMoneyByKey}
				/>
			</div>
		);
	}

	return (
		<div className="space-y-4 py-1 sm:space-y-6 sm:py-2">
			{attributeGroups.map((group) => {
				// Get options - all are selectable, availability shows stock status
				const options = getOptionsForAttribute(
					variants as SaleorVariant[],
					attributeGroups,
					currentSelections,
					group.slug,
				);

				// Check if this group is the one with no available options
				const isUnavailable = unavailableInfo?.slug === group.slug;
				const unavailableMessage = isUnavailable
					? `No ${group.name.toLowerCase()} available in ${unavailableInfo.blockedBy}`
					: undefined;

				return (
					<VariantSelector
						key={group.slug}
						label={group.name}
						options={options}
						selectedId={currentSelections[group.slug]}
						attributeSlug={group.slug}
						onSelect={handleSelect}
						unavailableMessage={unavailableMessage}
						isPending={isPending}
					/>
				);
			})}

			{/* Non-selection attributes (informational badges) */}
			<VariantAttributeBadges attributes={optionalAttributes} />
		</div>
	);
}
